/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, RadialBarChart, RadialBar, PieChart, Pie
} from 'recharts';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { fetchMonthlyRevenue, fetchSalesReport, fetchTopProductsReport } from '../services/api';

const Reports = () => {
  const [revenueData, setRevenueData] = useState([]);
  const [salesStats, setSalesStats] = useState(null);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('revenue');
  const containerRef = useRef(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [rev, sales, top] = await Promise.all([
          fetchMonthlyRevenue(),
          fetchSalesReport(),
          fetchTopProductsReport()
        ]);
        setRevenueData(rev || []);
        setSalesStats(sales);
        setTopProducts(top || []);
      } catch (error) {
        console.error("Error loading reports:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      const ctx = gsap.context(() => {
        gsap.fromTo('.report-card', 
          { y: 50, opacity: 0, scale: 0.97 },
          { y: 0, opacity: 1, scale: 1, duration: 1, stagger: 0.1, ease: 'expo.out', delay: 0.1, clearProps: 'all' }
        );
        gsap.fromTo('.stat-num', 
          { textContent: 0 },
          { textContent: 1, duration: 2, ease: 'power2.out', delay: 0.5, snap: { textContent: 1 } }
        );
      }, containerRef);
      return () => ctx.revert();
    }
  }, [loading]);

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0';
    if (amount >= 1000000) return `Rs ${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(0)}K`;
    return `Rs ${amount}`;
  };

  const statCards = [
    {
      label: 'Total Revenue',
      value: salesStats?.totalRevenue ? formatCurrency(salesStats.totalRevenue) : 'Rs 0',
      change: '+12.5%',
      positive: true,
      icon: 'ri-money-dollar-circle-fill',
      iconColor: 'text-amber-400',
      glow: 'rgba(251,191,36,0.15)',
      border: 'border-amber-500/10',
      bg: 'from-amber-500/5 to-transparent',
    },
    {
      label: 'Total Orders',
      value: salesStats?.totalSales || '0',
      change: '+8.2%',
      positive: true,
      icon: 'ri-shopping-bag-3-fill',
      iconColor: 'text-[#c82465]',
      glow: 'rgba(244,63,94,0.15)',
      border: 'border-[#c82465]/10',
      bg: 'from-[#c82465]/5 to-transparent',
    },
    {
      label: 'Avg Order Value',
      value: salesStats?.avgOrderValue ? formatCurrency(salesStats.avgOrderValue) : 'Rs 0',
      change: '+5.4%',
      positive: true,
      icon: 'ri-funds-box-fill',
      iconColor: 'text-violet-400',
      glow: 'rgba(167,139,250,0.15)',
      border: 'border-violet-500/10',
      bg: 'from-violet-500/5 to-transparent',
    },
    {
      label: 'Conversion Rate',
      value: '3.2%',
      change: '+1.2%',
      positive: true,
      icon: 'ri-line-chart-fill',
      iconColor: 'text-emerald-400',
      glow: 'rgba(52,211,153,0.15)',
      border: 'border-emerald-500/10',
      bg: 'from-emerald-500/5 to-transparent',
    },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#0d0d0d] border border-white/10 rounded-2xl px-5 py-4 shadow-2xl backdrop-blur-xl">
          <p className="text-[9px] font-bold text-white/30 uppercase tracking-[0.3em] mb-1">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} className="text-base font-bold italic" style={{ color: entry.color }}>
              {typeof entry.value === 'number' && entry.value > 1000 ? formatCurrency(entry.value) : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="h-[70vh] flex flex-col items-center justify-center space-y-6">
        <div className="relative w-24 h-24">
          <div className="absolute inset-0 border border-[#c82465]/20 rounded-full animate-ping" style={{ animationDuration: '2s' }}></div>
          <div className="absolute inset-0 border-t-2 border-[#c82465] rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="ri-bar-chart-box-fill text-2xl text-[#c82465]/40"></i>
          </div>
        </div>
        <p className="text-[9px] font-black text-white/20 uppercase tracking-[0.6em] animate-pulse">Generating Report</p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="pb-24 pt-2 space-y-8 max-w-[1600px] mx-auto px-0">

      {/* ══ HEADER ══ */}
      <div className="report-card relative overflow-hidden bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_20px_60px_rgba(0,0,0,0.3)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#c82465]/5 blur-[80px] rounded-full pointer-events-none"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-1.5 h-1.5 rounded-full bg-[#e0527a] animate-pulse"></div>
            <span className="text-[9px] font-black text-[#c82465]/80 uppercase tracking-[0.4em]">Business Intelligence v3.0</span>
          </div>
          <h1 className="text-2xl sm:text-4xl font-luxury italic text-white tracking-tight leading-none">
            Performance <span className="bg-gradient-to-r from-[#6b0f3a] to-[#c82465] bg-clip-text text-transparent">Intelligence</span>
          </h1>
          <p className="text-white/30 text-xs mt-3 max-w-md leading-relaxed">Comprehensive revenue analytics, sales velocity, and market performance — synthesized for strategic action.</p>
        </div>
        <div className="flex items-center gap-3 relative z-10 w-full sm:w-auto">
          <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 border border-white/10 text-[9px] font-black text-white/50 uppercase tracking-widest hover:text-white hover:bg-white/10 transition-all">
            <i className="ri-download-2-line text-sm"></i> Export
          </button>
          <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-gradient-to-r from-[#6b0f3a] to-[#c82465] text-white text-[9px] font-black uppercase tracking-widest hover:opacity-90 hover:shadow-[0_0_30px_rgba(192,38,211,0.3)] transition-all">
            <i className="ri-calendar-2-line text-sm"></i> Filter Period
          </button>
        </div>
      </div>

      {/* ══ STAT CARDS ══ */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {statCards.map((card, i) => (
          <div key={i} className={`report-card relative overflow-hidden bg-[#221b2d] border border-white/20 rounded-2xl sm:rounded-[1.5rem] p-5 sm:p-6 group hover:scale-[1.02] hover:bg-[#2a2238] transition-all duration-500 cursor-pointer shadow-xl`}
            style={{ boxShadow: `0 10px 40px ${card.glow}` }}>
            <div className="flex items-start justify-between mb-4">
              <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-white/[0.1] border border-white/30 flex items-center justify-center group-hover:scale-110 transition-transform duration-500`}>
                <i className={`${card.icon} text-lg sm:text-xl ${card.iconColor}`}></i>
              </div>
              <span className={`flex items-center gap-1 text-[9px] font-black px-2.5 py-1 rounded-full ${card.positive ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-500/40' : 'bg-[#c82465]/30 text-[#ff4d94] border border-[#c82465]/40'}`}>
                <i className={`${card.positive ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-xs`}></i>
                {card.change}
              </span>
            </div>
            <p className="text-[8px] sm:text-[10px] font-bold text-gray-300 uppercase tracking-[0.3em] mb-1">{card.label}</p>
            <p className="text-xl sm:text-3xl font-bold text-white stat-num truncate drop-shadow-md">{card.value}</p>
          </div>
        ))}
      </div>

      {/* ══ MAIN CHART ══ */}
      <div className="report-card grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Area Chart */}
        <div className="lg:col-span-2 bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.3)] overflow-hidden relative">
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#c82465]/5 blur-[60px] rounded-full pointer-events-none"></div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-8 gap-4 relative z-10">
            <div>
              <h3 className="text-xl sm:text-2xl font-luxury italic text-white">Revenue Dynamics</h3>
              <p className="text-[9px] text-white/30 font-bold uppercase tracking-[0.3em] mt-1">Monthly growth trajectory</p>
            </div>
            <div className="flex gap-2">
              {['revenue', 'sales'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)}
                  className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'bg-gradient-to-r from-[#6b0f3a] to-[#c82465] text-white shadow-lg' : 'bg-white/5 text-white/30 hover:text-white border border-white/5'}`}>
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <div className="h-[280px] sm:h-[320px] relative z-10">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#c82465" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#c82465" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="salesGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.03)" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 9, fontWeight: 700 }} dy={10} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 9, fontWeight: 700 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey={activeTab === 'revenue' ? 'revenue' : 'sales'}
                  stroke={activeTab === 'revenue' ? '#c82465' : '#a855f7'}
                  strokeWidth={2.5}
                  fill={activeTab === 'revenue' ? 'url(#revGrad)' : 'url(#salesGrad)'}
                  dot={{ r: 3.5, fill: activeTab === 'revenue' ? '#c82465' : '#a855f7', strokeWidth: 2, stroke: '#0a0a0a' }}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: '#0a0a0a' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.3)] flex flex-col">
          <div className="mb-6">
            <h3 className="text-xl font-luxury italic text-white">Elite Assets</h3>
            <p className="text-[9px] text-white/30 font-bold uppercase tracking-[0.3em] mt-1">Top performing products</p>
          </div>
          <div className="flex-1 space-y-4 overflow-y-auto no-scrollbar">
            {topProducts.length > 0 ? topProducts.slice(0, 6).map((product, i) => (
              <div key={i} className="flex items-center gap-3 group/item cursor-pointer hover:bg-white/[0.02] rounded-xl p-2 -mx-2 transition-all">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg overflow-hidden bg-white/5 border border-white/10">
                  <img src={product.image || `https://images.unsplash.com/photo-1541643600914-78b084683601?w=80&q=80`}
                    alt={product.name} className="w-full h-full object-cover opacity-70 group-hover/item:opacity-100 transition-opacity" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white/80 group-hover/item:text-white transition-colors truncate">{product.name || 'Luxury Perfume'}</p>
                  <p className="text-[9px] text-white/30 font-bold uppercase tracking-widest">{product.sales || 0} sold</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-xs font-black text-white italic">{formatCurrency(product.revenue || 0)}</p>
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <div className="w-10 h-1 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#c82465] to-violet-500 rounded-full" style={{ width: `${Math.min(100, ((product.sales || 0) / (topProducts[0]?.sales || 1)) * 100)}%` }}></div>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              <div className="flex-1 flex items-center justify-center text-center py-10">
                <div>
                  <i className="ri-inbox-line text-3xl text-white/10 block mb-3"></i>
                  <p className="text-[9px] text-white/20 font-bold uppercase tracking-widest">No products data</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ BOTTOM ROW ══ */}
      <div className="report-card grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* Bar Chart */}
        <div className="lg:col-span-2 bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.3)] relative overflow-hidden">
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-violet-500/5 blur-[60px] rounded-full pointer-events-none"></div>
          <div className="mb-8 relative z-10">
            <h3 className="text-xl sm:text-2xl font-luxury italic text-white">Sector Performance</h3>
            <p className="text-[9px] text-white/30 font-bold uppercase tracking-[0.3em] mt-1">Monthly sales volume breakdown</p>
          </div>
          <div className="h-[240px] relative z-10">
            <ResponsiveContainer width="100%" height="100%" minWidth={0}>
              <BarChart data={revenueData.slice(-6)} barSize={28} margin={{ top: 0, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="rgba(255,255,255,0.03)" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 9, fontWeight: 700 }} dy={8} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: '#475569', fontSize: 9, fontWeight: 700 }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(255,255,255,0.02)', radius: 8 }} />
                <Bar dataKey="sales" radius={[8, 8, 0, 0]}>
                  {revenueData.slice(-6).map((entry, index) => (
                    <Cell key={`cell-${index}`}
                      fill={index % 3 === 0 ? '#c82465' : index % 3 === 1 ? '#a855f7' : '#f59e0b'}
                      fillOpacity={0.75}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-6 mt-4 relative z-10">
            {[['#c82465', 'High Season'], ['#a855f7', 'Mid Season'], ['#f59e0b', 'Low Season']].map(([color, label]) => (
              <div key={label} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }}></div>
                <span className="text-[8px] font-bold text-white/30 uppercase tracking-widest">{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* KPI Summary */}
        <div className="bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.3)] flex flex-col gap-4">
          <div className="mb-2">
            <h3 className="text-xl font-luxury italic text-white">Market Mastery</h3>
            <p className="text-[9px] text-white/30 font-bold uppercase tracking-[0.3em] mt-1">Key performance indices</p>
          </div>

          {/* Progress Bars */}
          {[
            { label: 'Luxury Perfumes', pct: 85, color: 'from-[#c82465] to-[#e0527a]' },
            { label: 'Elite Collections', pct: 68, color: 'from-violet-500 to-violet-400' },
            { label: 'Private Blends', pct: 52, color: 'from-amber-500 to-amber-400' },
            { label: 'Gift Sets', pct: 39, color: 'from-emerald-500 to-emerald-400' },
          ].map((item, i) => (
            <div key={i} className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold text-white/50 uppercase tracking-widest">{item.label}</span>
                <span className="text-[9px] font-black text-white italic">{item.pct}%</span>
              </div>
              <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                <div className={`h-full bg-gradient-to-r ${item.color} rounded-full transition-all duration-1000`}
                  style={{ width: `${item.pct}%` }}></div>
              </div>
            </div>
          ))}

          {/* Achievement */}
          <div className="mt-auto pt-4 border-t border-white/5">
            <div className="flex items-center gap-3 p-4 rounded-2xl bg-emerald-500/5 border border-emerald-500/10">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                <i className="ri-award-fill text-emerald-400 text-lg"></i>
              </div>
              <div>
                <p className="text-[9px] font-black text-emerald-400 uppercase tracking-widest">Outperforming</p>
                <p className="text-xs font-bold text-white/60 mt-0.5">Luxury index by <span className="text-emerald-400 font-black">+24.8%</span></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══ AI RECOMMENDATIONS ══ */}
      <div className="report-card bg-gradient-to-br from-white/[0.03] to-transparent border border-white/5 rounded-[2rem] p-6 sm:p-8 shadow-[0_30px_80px_rgba(0,0,0,0.3)] relative overflow-hidden">
        <div className="absolute -top-10 right-10 w-40 h-40 bg-violet-500/8 blur-[60px] rounded-full pointer-events-none"></div>
        <div className="absolute -bottom-10 left-10 w-40 h-40 bg-[#c82465]/5 blur-[60px] rounded-full pointer-events-none"></div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-5 h-5 rounded-md bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                <i className="ri-sparkling-fill text-violet-400 text-[10px]"></i>
              </div>
              <span className="text-[9px] font-black text-violet-400 uppercase tracking-[0.4em]">AI Powered</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-luxury italic text-white">Strategic Recommendations</h3>
          </div>
          <button className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#6b0f3a] to-[#c82465] text-white text-[9px] font-black uppercase tracking-widest hover:opacity-90 transition-all self-start sm:self-auto">
            <i className="ri-refresh-line"></i> Regenerate
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10">
          {[
            { icon: 'ri-global-line', color: 'text-sky-400', bg: 'bg-sky-500/10 border-sky-500/20', title: 'MENA Expansion', desc: 'Scale digital presence in Middle East & North Africa. High-income segments show 34% engagement growth.' },
            { icon: 'ri-vip-diamond-line', color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20', title: 'Q4 Personalization', desc: 'Pivot towards personalized scents for Q4. Algorithm projects a 22% potential lift in conversion.' },
            { icon: 'ri-group-2-line', color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20', title: 'Elite Membership', desc: 'Expand loyalty program benefits. Retained members generate 3.8x higher lifetime value than one-time buyers.' },
          ].map((rec, i) => (
            <div key={i} className={`p-5 rounded-2xl border ${rec.bg} hover:scale-[1.02] transition-all duration-500 cursor-pointer group`}>
              <div className={`w-10 h-10 rounded-xl ${rec.bg} border flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <i className={`${rec.icon} ${rec.color} text-lg`}></i>
              </div>
              <h4 className={`text-xs font-black ${rec.color} uppercase tracking-widest mb-2`}>{rec.title}</h4>
              <p className="text-[11px] text-white/40 leading-relaxed font-medium">{rec.desc}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default Reports;
